import { initializeApp } from './app/app.module';
import { provideAnimations } from '@angular/platform-browser/animations';
import { UserProfileService } from './services/user-profile.service';
import { OktaAuthService } from './services/okta-auth.service';
import { DeepLinkService } from './services/deep-link.service';
import { DemoModeService } from './services/demo-mode.service';
import { RuntimeConfigService } from './services/runtime-config.service';
import { KeyboardService } from './services/keyboard.service';
import { UserService, USER_SERVICE_TOKEN } from './services/user-service.service';
import { AppService } from './services/app.service';
import { PushNotificationService } from './services/push-notification-service';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { AuthInterceptor } from './app/auth/auth.interceptor';
import { AnalyticsInterceptor } from './interceptors/analytics.interceptor';
import { DiagnosticsInterceptor } from './interceptors/diagnostics.interceptor';
import { TRUST_DIAG_OVERLAY } from './services/trust-diagnostics';
import { ENVIRONMENT } from './services/environment.token';
import { environment } from './environments/environment';
import { RouteReuseStrategy } from '@angular/router';
import { CustomRouteReuseStrategy } from './app/custom-route-reuse-strategy';
import { APP_INITIALIZER, importProvidersFrom } from '@angular/core';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MAT_DIALOG_DEFAULT_OPTIONS, MatDialogConfig } from '@angular/material/dialog';
import { AppRoutingModule } from './app/app-routing.module';
import { AppComponent } from './app/app.component';
import { HybridStorageService } from './app/services/hybrid-storage.service';
import { AuthStorageService } from './app/services/auth-storage.service';
import { MarkdownModule } from 'ngx-markdown';
import { provideTranslateService, TranslateCompiler } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { TranslateMessageFormatCompiler } from 'ngx-translate-messageformat-compiler';
import { FormlyModule } from '@ngx-formly/core';
import { FormlyBootstrapModule } from '@ngx-formly/bootstrap';
import { FormDatePickerComponent } from './app/apps/posts/post-add-ons/post-forms/form-datepicker/form-datepicker.component';
import { TranslationService } from './services/translation.service';

// Register locale data for internationalization
import { registerLocaleData } from '@angular/common';
import localeEs from '@angular/common/locales/es';
import localeAr from '@angular/common/locales/ar';
import localeBn from '@angular/common/locales/bn';
import localeSw from '@angular/common/locales/sw';
import localeTa from '@angular/common/locales/ta';
import localeTe from '@angular/common/locales/te';
import localeUr from '@angular/common/locales/ur';
import localeVi from '@angular/common/locales/vi';

registerLocaleData(localeEs, 'es');
registerLocaleData(localeAr, 'ar');
registerLocaleData(localeBn, 'bn');
registerLocaleData(localeSw, 'sw');
registerLocaleData(localeTa, 'ta');
registerLocaleData(localeTe, 'te');
registerLocaleData(localeUr, 'ur');
registerLocaleData(localeVi, 'vi');

bootstrapApplication(AppComponent, {
    providers: [
        importProvidersFrom(
            BrowserModule,
            AppRoutingModule,
            HttpClientModule,
            MarkdownModule.forRoot(),
            FormlyModule.forRoot({
                types: [
                    { name: 'datepicker', component: FormDatePickerComponent }
                ]
            }),
            FormlyBootstrapModule
        ),
        provideTranslateService({
            fallbackLang: 'en',
            compiler: {
                provide: TranslateCompiler,
                useClass: TranslateMessageFormatCompiler
            }
        }),
        provideTranslateHttpLoader({
            prefix: './assets/i18n/',
            suffix: '.json'
        }),
        provideAnimations(),
        provideNativeDateAdapter(),
        {
            provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
            useValue: {
                subscriptSizing: 'dynamic',
                appearance: 'outline'
            }
        },
        {
            // Must spread MatDialogConfig: these options replace Material's defaults rather than merge with them
            provide: MAT_DIALOG_DEFAULT_OPTIONS,
            useValue: { ...new MatDialogConfig(), maxHeight: '90vh' }
        },
        UserProfileService,
        OktaAuthService,
        AuthStorageService,
        DeepLinkService,
        DemoModeService,
        RuntimeConfigService,
        KeyboardService,
        UserService,
        AppService,
        PushNotificationService,
        // TEMPORARY diagnostics. Gated so a single flag removes the overlay AND this
        // interceptor from the DI graph entirely.
        ...(TRUST_DIAG_OVERLAY ? [{ provide: HTTP_INTERCEPTORS, useClass: DiagnosticsInterceptor, multi: true }] : []),
        { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: AnalyticsInterceptor, multi: true },
        { provide: ENVIRONMENT, useValue: environment },
        { provide: USER_SERVICE_TOKEN, useClass: UserService },
        { provide: RouteReuseStrategy, useClass: CustomRouteReuseStrategy },
        // Updated APP_INITIALIZER to chain both
        {
            provide: APP_INITIALIZER,
            useFactory: initializeApp,
            deps: [HybridStorageService, AuthStorageService, RuntimeConfigService, OktaAuthService, DeepLinkService, KeyboardService, TranslationService],
            multi: true
        }
    ]
})
  .catch(err => console.error(err)); 
